import React from 'react'
import { Table, Button, Space, Typography, Switch, Tooltip, theme } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'
import { FlattenedRoomData } from './types'
import { renderRoomThumb, renderDescriptionPreview, renderTagsPreviewWithPopover } from './utils'
import { SortableRow, DragHandle } from './SortableRow'

const { Text } = Typography

type RoomDesktopTableProps = {
  rooms: FlattenedRoomData[]
  loading: boolean
  updatingRoomCode: string | null
  isSortMode: boolean
  onShowDetail: (room: FlattenedRoomData) => void
  onEdit: (room: FlattenedRoomData) => void
  onToggleEnabled: (room: FlattenedRoomData, enabled: boolean) => void
  onDragEnd: (event: DragEndEvent) => void
}

const RoomDesktopTable: React.FC<RoomDesktopTableProps> = ({
  rooms,
  loading,
  updatingRoomCode,
  isSortMode,
  onShowDetail,
  onEdit,
  onToggleEnabled,
  onDragEnd,
}) => {
  const { token } = theme.useToken()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
  )

  const dragHandleColumn = {
    key: 'sort',
    width: 40,
    align: 'center' as const,
    render: () => <DragHandle />,
  }

  const dataColumns = [
    {
      title: 'Image',
      dataIndex: 'cover_image',
      key: 'cover_image',
      width: 100,
      fixed: 'left' as const,
      render: (_: any, record: FlattenedRoomData) => renderRoomThumb(record, onShowDetail),
    },
    {
      title: 'Room Info',
      key: 'info',
      width: 200,
      render: (_: any, record: FlattenedRoomData) => (
        <Space orientation="vertical" size={0} style={{ maxWidth: '100%' }}>
          <Text strong ellipsis={{ tooltip: record.display_name }} style={{ color: token.colorPrimary, maxWidth: '100%' }}>{record.display_name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.room_code}</Text>
        </Space>
      ),
    },
    {
      title: 'Highlights',
      dataIndex: 'highlights',
      key: 'highlights',
      width: 180,
      render: (tags: string[]) => renderTagsPreviewWithPopover(tags, 'geekblue', 2),
    },
    {
      title: 'Facilities',
      dataIndex: 'amenities',
      key: 'amenities',
      width: 220,
      render: (tags: string[]) => renderTagsPreviewWithPopover(tags, 'green', 3),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (html: string) => renderDescriptionPreview(html),
    },
    {
      title: 'Avail',
      children: [
        {
          title: 'Max',
          dataIndex: 'max_avail',
          key: 'max_avail',
          width: 65,
          align: 'center' as const,
          render: (v: number | null | undefined) => v != null ? v : '-',
        },
        {
          title: 'Offset',
          dataIndex: 'avail_offset',
          key: 'avail_offset',
          width: 65,
          align: 'center' as const,
          render: (v: number | null | undefined) => v != null ? v : '-',
        },
      ],
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: FlattenedRoomData) => (
        <Tooltip title={record.enabled ? 'Disable Room' : 'Enable Room'}>
          <Switch
            checked={record.enabled}
            onChange={(checked) => onToggleEnabled(record, checked)}
            loading={updatingRoomCode === record.room_code}
            size="small"
            style={{ width: 40 }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_: any, record: FlattenedRoomData) => (
        <Tooltip title="Edit">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
        </Tooltip>
      ),
    },
  ]

  const columns = isSortMode ? [dragHandleColumn, ...dataColumns] : dataColumns

  const table = (
    <Table
      columns={columns}
      dataSource={rooms}
      rowKey="room_code"
      size="small"
      loading={loading}
      scroll={{ x: isSortMode ? 1370 : 1330 }}
      pagination={isSortMode ? false : { pageSize: 10 }}
      components={isSortMode ? { body: { row: SortableRow } } : undefined}
    />
  )

  if (!isSortMode) return table

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={rooms.map(r => r.room_code)} strategy={verticalListSortingStrategy}>
        {table}
      </SortableContext>
    </DndContext>
  )
}

export default RoomDesktopTable
